import { brandConfig } from '../../../config/brands';
import type { MaterialsPanelMode } from '../../../stores/materialsPanelStore';
import { FilterSection } from '../../common/FilterSection';
import { MultiSelectDropdown } from '../../common/MultiSelectDropdown';
import { SingleSelectDropdown } from '../../common/SingleSelectDropdown';
import type { MaterialsFiltersApi } from './useMaterialsFilters';
import type { FilterOptionsApi, MergedFacets } from './useFilterOptions';

interface Props {
  filters: MaterialsFiltersApi;
  options: FilterOptionsApi;
  facets: MergedFacets | undefined;
  sourceFilterOptions: { value: string; label: string; divider?: boolean }[];
  workflowStages: Array<{ id: string; label: string }>;
  workflowStageEnabled: boolean;
  mode: MaterialsPanelMode;
  imageType: 'main' | 'sub';
  hideFileTypeFilter: boolean;
  hideSourceTypeFilter: boolean;
  /** アスペクト比フィルタを隠す (音声ピッカーなど比率が意味を持たないとき) */
  hideAspectRatioFilter?: boolean;
  hideAmazonComplianceFilter: boolean;
  showMaxDurationFilter: boolean;
  /** 検索ボックスを最初から展開表示し、開閉シェブロンを隠す (ピッカー用) */
  alwaysShowSearch?: boolean;
  /** 取得元フィルタのみ表示する (usodeki: 中高生向けに絞り込みを最小化) */
  sourceOnly?: boolean;
  /** ナレーションの生成元シナリオの選択肢 (ナレーション追加ピッカーのみ)。無ければシナリオ絞り込みを出さない */
  narrationScenarioOptions?: { value: string; label: string; badge?: number }[];
  /** 「更新」ボタン */
  onRefresh: () => void;
  isRefreshing: boolean;
}

/**
 * 素材一覧のフィルタセクション (ブランド/商品/種別/アスペクト比/取得元/タグ/
 * 制作進捗/検索/最大尺/Amazon適合)。状態は useMaterialsFilters が正本。
 */
export function MaterialsFilterBar({
  filters,
  options,
  facets,
  sourceFilterOptions,
  workflowStages,
  workflowStageEnabled,
  mode,
  imageType,
  hideFileTypeFilter,
  hideSourceTypeFilter,
  hideAspectRatioFilter = false,
  hideAmazonComplianceFilter,
  showMaxDurationFilter,
  alwaysShowSearch = false,
  sourceOnly = false,
  narrationScenarioOptions,
  onRefresh,
  isRefreshing,
}: Props) {
  const {
    searchQuery, setSearchQuery,
    overflowMenuOpen, setOverflowMenuOpen,
    fileTypeFilter, setFileTypeFilter,
    aspectRatioFilter, setAspectRatioFilter,
    sourceTypeFilter, setSourceTypeFilter,
    filterExpanded, setFilterExpanded,
    productFilter, setProductFilter,
    brandFilter, setBrandFilter,
    tagFilter, setTagFilter,
    workflowStageFilter, setWorkflowStageFilter,
    maxDurationFilter, setMaxDurationFilter,
    narrationScenarioFilter, setNarrationScenarioFilter,
    amazonProductImageFilter, setAmazonProductImageFilter,
    clearFilters,
    hasActiveFilters,
    overflowHasActive,
  } = filters;
  const { allProductOptions, filteredProductOptions, filteredBrandOptions, allTags, isLoadingBrands } = options;

  // usodeki: 取得元フィルタのみを出す簡素版。ブランド/商品/種別/タグ等は非表示。
  if (sourceOnly) {
    return (
      <FilterSection
        expanded={filterExpanded}
        onToggleExpanded={() => setFilterExpanded(!filterExpanded)}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        headerRight={
          <button
            type="button"
            className="refresh-button"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing && <span className="spinner"></span>}
            更新
          </button>
        }
      >
        <div className="filter-row filter-row-secondary">
          <MultiSelectDropdown
            label="取得元"
            dropdownClassName="filter-dropdown-menu"
            fitContent
            options={sourceFilterOptions}
            selectedValues={sourceTypeFilter}
            onChange={setSourceTypeFilter}
          />
        </div>
      </FilterSection>
    );
  }

  // 最大尺(秒)コントロール。showMaxDurationFilter のときは表のフィルタ行に、
  // それ以外は 3 点リーダーの中に描画するため共通化する。
  const maxDurationControl = (
    <div className="max-duration-filter">
      <span>最大尺</span>
      <input
        type="number"
        min={1}
        step={1}
        inputMode="numeric"
        className="max-duration-input"
        value={maxDurationFilter ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          const n = Number(v);
          setMaxDurationFilter(v === '' || Number.isNaN(n) || n <= 0 ? null : n);
        }}
      />
      <span>秒以内</span>
      {maxDurationFilter != null && (
        <button
          type="button"
          className="max-duration-clear"
          onClick={() => setMaxDurationFilter(null)}
          title="クリア"
          aria-label="最大尺フィルタをクリア"
        >
          ×
        </button>
      )}
    </div>
  );

  return (
    <FilterSection
      expanded={filterExpanded}
      onToggleExpanded={() => setFilterExpanded(!filterExpanded)}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={clearFilters}
      headerRight={
        <button
          type="button"
          className="refresh-button"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing && (
            <span className="spinner"></span>
          )}
          更新
        </button>
      }
    >
      {/* 1列目: ブランド, 商品, シナリオ (ナレーション追加ピッカーのみ) */}
      <div className="filter-row">
        <SingleSelectDropdown
          label={isLoadingBrands ? 'ブランド (読込中...)' : 'ブランド'}
          className="brand-filter-dropdown"
          dropdownClassName="filter-dropdown-menu"
          fitContent
          options={[
            { value: '', label: 'すべて' },
            ...(facets?.has_no_brand_items !== false ? [{ value: '__no_brand__', label: 'ブランドなし' }] : []),
            ...filteredBrandOptions.map((item) => ({
              value: item.brand_id,
              label: item.label,
              ...(item.isFavorite ? { iconAfter: '/star_1_gold.svg' } : {}),
            })),
          ]}
          value={brandFilter}
          onChange={(v) => {
            setBrandFilter(v);
            // ブランド変更時、選択中の商品が新ブランドに属さなければリセット
            if (productFilter && v && v !== '__no_brand__') {
              const selectedProduct = allProductOptions.find(opt => opt.value === productFilter);
              if (selectedProduct && selectedProduct.brand_id !== v) {
                setProductFilter('');
              }
            }
          }}
        />
        <SingleSelectDropdown
          label={brandConfig.labels.productShort}
          labelOnly
          className="product-filter-dropdown"
          dropdownClassName="filter-dropdown-menu"
          fitContent
          options={[
            { value: '', label: 'すべて' },
            ...(facets?.has_no_product_items !== false ? [{ value: '__no_product__', label: `${brandConfig.labels.productShort}なし` }] : []),
            ...filteredProductOptions,
          ]}
          value={productFilter}
          onChange={setProductFilter}
        />
        {/* ナレーションの生成元シナリオ (ナレーション追加ピッカーのみ)。ブランド → 案件 → シナリオの順に絞る */}
        {narrationScenarioOptions && (
        <SingleSelectDropdown
          label="シナリオ"
          className="narration-scenario-filter-dropdown"
          dropdownClassName="filter-dropdown-menu"
          fitContent
          options={[{ value: '', label: 'すべて' }, ...narrationScenarioOptions]}
          value={narrationScenarioFilter}
          onChange={setNarrationScenarioFilter}
        />
        )}
      </div>
      {/* 2列目: 種別, アスペクト比, 取得元, タグ, 検索 */}
      <div className="filter-row filter-row-secondary">
        {!hideFileTypeFilter && (
        <MultiSelectDropdown
          label="種別"
          dropdownClassName="filter-dropdown-menu"
          fitContent
          options={[
            { value: 'image', label: '画像' },
            { value: 'video', label: '動画' },
            { value: 'audio', label: '音声' },
          ]}
          selectedValues={fileTypeFilter}
          onChange={setFileTypeFilter}
        />
        )}
        {!hideAspectRatioFilter && (
        <MultiSelectDropdown
          label="アスペクト比"
          dropdownClassName="filter-dropdown-menu"
          fitContent
          options={[
            { value: '16:9', label: '16:9' },
            { value: '9:16', label: '9:16' },
            { value: '1:1', label: '1:1' },
            { value: '7:4', label: '7:4' },
            { value: '4:7', label: '4:7' },
            { value: '1.77:1', label: '1.77:1' },
            { value: '1:1.77', label: '1:1.77' },
            { value: 'other', label: 'その他' },
          ].filter(opt => !facets?.aspect_ratios || facets.aspect_ratios.includes(opt.value))}
          selectedValues={aspectRatioFilter}
          onChange={setAspectRatioFilter}
        />
        )}
        {!hideSourceTypeFilter && (
        <MultiSelectDropdown
          label="取得元"
          dropdownClassName="filter-dropdown-menu"
          fitContent
          options={sourceFilterOptions}
          selectedValues={sourceTypeFilter}
          onChange={setSourceTypeFilter}
        />
        )}
        <MultiSelectDropdown
          label={brandConfig.labels.tag}
          dropdownClassName="filter-dropdown-menu"
          fitContent
          options={allTags.map((tag) => ({ value: tag, label: tag }))}
          selectedValues={tagFilter}
          onChange={setTagFilter}
        />
        {workflowStageEnabled && (
          <MultiSelectDropdown
            label="制作進捗"
            dropdownClassName="filter-dropdown-menu"
            fitContent
            options={[
              ...(facets?.has_no_workflow_stage_items !== false
                ? [{ value: '__no_workflow_stage__', label: '未設定' }]
                : []),
              ...workflowStages
                .filter((stage) =>
                  !facets?.workflow_stage_ids
                    ? true
                    : facets.workflow_stage_ids.includes(stage.id),
                )
                .reverse()
                .map((stage) => ({ value: stage.id, label: stage.label })),
            ]}
            selectedValues={workflowStageFilter}
            onChange={setWorkflowStageFilter}
          />
        )}
        {/* シェブロン: 非推奨フィルタ (検索 / 最大尺) をインラインで開閉。
            alwaysShowSearch のときは常時展開なのでシェブロン自体を出さない。 */}
        {!alwaysShowSearch && (
        <button
          type="button"
          className={`materials-overflow-button${overflowHasActive ? ' has-active' : ''}${overflowMenuOpen ? ' is-open' : ''}`}
          onClick={() => setOverflowMenuOpen(!overflowMenuOpen)}
          aria-label="その他のフィルタ"
          aria-expanded={overflowMenuOpen}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        )}
      </div>
      {/* ピッカー (検索を常時表示) では検索の前で改行し、検索・最大尺を絞り込みの次の行に並べる */}
      {alwaysShowSearch && <div className="filter-row-break" aria-hidden="true" />}
      {/* 検索・最大尺 (非推奨フィルタ) をインラインで展開 */}
      {(overflowMenuOpen || alwaysShowSearch) && (
        <div className="filter-row materials-overflow-expanded">
          <div className="materials-overflow-item">
            {/* 見出しは虫眼鏡アイコン (シナリオブックの検索ボタンと同じ形) */}
            <span className="materials-overflow-label materials-overflow-label-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="search"
              aria-label="検索"
              className="search-box"
              placeholder={`${brandConfig.labels.productName}やメモで検索`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>
          {!showMaxDurationFilter && (
            <div className="materials-overflow-item">
              <span className="materials-overflow-label">最大尺</span>
              {maxDurationControl}
            </div>
          )}
        </div>
      )}
      {mode === 'select' && brandConfig.features.amazonIntegration && !hideAmazonComplianceFilter && (
        <div className="filter-row">
          <label className="filter-checkbox-label">
            <input
              type="checkbox"
              checked={amazonProductImageFilter}
              onChange={(e) => setAmazonProductImageFilter(e.target.checked)}
            />
            <span>Amazon{imageType === 'main' ? 'メイン' : 'サブ'}画像の推奨規定に合致</span>
          </label>
        </div>
      )}
      {/* 最大尺(秒)フィルタ: ストーリーボード/ブランドカット等では表に出す。
          それ以外は非推奨フィルタとして 3 点リーダー内に隠す。 */}
      {showMaxDurationFilter && (
        <div className="filter-row">
          {maxDurationControl}
        </div>
      )}
    </FilterSection>
  );
}
